import { useCallback, useEffect, useState } from 'react'

import * as Sentry from '@sentry/browser'
import { isEqual, parseISO } from 'date-fns'
import { keyBy } from 'lodash'

import {
  SubmittedWorklog,
  submittedWorklogsSelector,
} from '../actions/submittableWorkLogsSelector'

import { useSelector } from './useSelector'

type JiraWorklog = {
  id: string
  comment: string
  started: string
  timeSpentSeconds: number
}

const getJiraWorklogs = async (
  jiraWorklogsIds: string[]
): Promise<JiraWorklog[]> => {
  try {
    const response = await fetch('/api/jiraWorklogs', {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      redirect: 'follow',
      referrer: 'no-referrer',
      body: JSON.stringify({ jiraWorklogsIds }),
    })
    const { jiraWorklogs } = await response.json()
    return jiraWorklogs
  } catch (e) {
    Sentry.captureException(e)
    return []
  }
}

interface AreSubmittedWorklogsInJira {
  allWorklogsInJira: boolean
  worklogsNotInJira: SubmittedWorklog[]
  worklogsDiffFromJira: SubmittedWorklog[]
}

const getAreSubmittedWorklogsInJira = async (
  worklogs: SubmittedWorklog[]
): Promise<AreSubmittedWorklogsInJira> => {
  const jiraWorklogsIds = worklogs.map((worklog) => worklog.jiraId)
  const jiraWorklogs = await getJiraWorklogs(jiraWorklogsIds)
  const jiraWorklogsById = keyBy(jiraWorklogs, 'id')

  const worklogsNotInJira = worklogs.filter((worklog) => {
    return !jiraWorklogsById[worklog.jiraId]
  })
  const worklogsDiffFromJira = worklogs.filter((worklog) => {
    const jiraWorklog = jiraWorklogsById[worklog.jiraId]
    return (
      jiraWorklog &&
      (worklog.comment !== jiraWorklog.comment ||
        !isEqual(
          parseISO(worklog.dateStarted),
          parseISO(jiraWorklog.started)
        ) ||
        worklog.timeSpentSeconds !== jiraWorklog.timeSpentSeconds)
    )
  })

  return {
    allWorklogsInJira:
      worklogsNotInJira.length === 0 && worklogsDiffFromJira.length === 0,
    worklogsNotInJira,
    worklogsDiffFromJira,
  }
}

export const useAreSubmittedWorklogsInJira = () => {
  const [worklogsInJira, setWorklogsInJira] =
    useState<AreSubmittedWorklogsInJira>({
      allWorklogsInJira: true,
      worklogsNotInJira: [],
      worklogsDiffFromJira: [],
    })
  const [worklogsInJiraLoading, setWorklogsInJiraLoading] = useState(true)

  const worklogs = useSelector(submittedWorklogsSelector)

  const updateAreSubmittedWorklogsInJira = useCallback(async () => {
    setWorklogsInJiraLoading(true)
    const worklogsInJira = await getAreSubmittedWorklogsInJira(worklogs)
    setWorklogsInJira(worklogsInJira)
    setWorklogsInJiraLoading(false)
  }, [worklogs])

  useEffect(() => {
    updateAreSubmittedWorklogsInJira()
  }, [updateAreSubmittedWorklogsInJira])

  return {
    ...worklogsInJira,
    worklogsInJiraLoading,
  }
}
